












































import { Component, Mixins, Watch } from "vue-property-decorator";
import { mapGetters } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import ExchangeRatesInjectorMixin from "@/mixins/exchangeRatesInjectorMixin";
import BrandsStore from "@/mixins/brandsStore";

// Providers
import { ICurrency } from "@/models/constants";
import { IExchangeRate, IExchangeRateMatrix } from "@/models/exchangeRates";

@Component({
  components: {
    "manual-exchange-rate": () =>
      import(
        "@/components/app/admin/settings/currencies/manualExchangeRate.vue"
      )
  },
  computed: {
    ...mapGetters({
      isOrgMode: "brand/isMultibrand"
    })
  }
})
export default class ManualExchangeRatesMatrix extends Mixins(
  ExchangeRatesInjectorMixin,
  BrandsStore
) {
  // Getters

  public isOrgMode!: boolean;

  // Data

  public activeBaseCurrencyId: ICurrency["id"] = "";
  public isProcessing = false;
  public initialExchangeRates: IExchangeRateMatrix[] = [];
  public currentExchangeRates: IExchangeRateMatrix[] = [];

  // Life-cycle methods

  public created() {
    this.initiateBaseCurrency();
  }

  // Methods

  public initiateBaseCurrency() {
    if (this.isOrgMode) {
      this.activeBaseCurrencyId =
        this.availableExchangeRates[0]?.base_currency_id ||
        this.$exchangeRatesData.orgBrandCurrencies[0]?.id;
      return;
    }

    this.activeBaseCurrencyId = this.brandCurrencyId;
  }

  public initiateExchangeRates() {
    this.initialExchangeRates = _.map(
      this.filteredCurrencies,
      filteredCurrency => {
        const orgRate = _.find(this.orgExchangeRates, orgExchangeRate => {
          return (
            orgExchangeRate.base_currency_id === this.activeBaseCurrencyId &&
            orgExchangeRate.currency_id === filteredCurrency.id
          );
        });

        const brandRate = _.find(this.brandExchangeRates, brandExchangeRate => {
          return (
            brandExchangeRate.base_currency_id === this.activeBaseCurrencyId &&
            brandExchangeRate.currency_id === filteredCurrency.id
          );
        });

        if (this.isOrgMode) {
          return {
            active: orgRate?.active || false,
            code: filteredCurrency.code,
            currency_id: filteredCurrency.id,
            id: orgRate?.id || null,
            isDynamic: !orgRate,
            orgValue: orgRate?.rate || null,
            value: null
          };
        }

        return {
          active: brandRate?.active || orgRate?.active || false,
          code: filteredCurrency.code,
          currency_id: filteredCurrency.id,
          id: brandRate?.id || null,
          isDynamic: !brandRate,
          orgValue: orgRate?.rate || null,
          value: brandRate?.rate || null
        };
      }
    );

    this.currentExchangeRates = _.cloneDeep(this.initialExchangeRates);
  }

  public onChange() {
    this.$emit(
      "change",
      !_.isEmpty(this.removedExchangeRatesIds) ||
        !_.isEmpty(this.updatedExchangeRates)
    );
  }

  public async save() {
    try {
      if (this.hasEmptyValue) return;

      this.isProcessing = true;

      await Promise.all([
        ...this.addNewExchangeRates(),
        ...this.removeOldExchangeRates()
      ]);
      await this.$exchangeRatesMethods.reloadData();
      this.initiateExchangeRates();
      this.$emit("predefinedSettingsAreChanged", false);
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this,
        showToast: false
      });

      this.$emit("error", error);
      throw error;
    } finally {
      this.isProcessing = false;
    }
  }

  public revertToInitialState() {
    this.initiateBaseCurrency();
    this.initiateExchangeRates();
  }

  public addNewExchangeRates() {
    return this.updatedExchangeRates.length
      ? _.map(this.updatedExchangeRates, updatedExchangeRate => {
          return this.$exchangeRatesMethods.createRate({
            active: updatedExchangeRate.active,
            rate: updatedExchangeRate.value as number,
            currency_code:
              _.find(this.$exchangeRatesData.currencies, [
                "id",
                updatedExchangeRate.currency_id
              ])?.code || "",
            base_currency_code: this.activeBaseCurrency?.code || ""
          });
        })
      : [Promise.resolve()];
  }

  public removeOldExchangeRates() {
    return this.removedExchangeRatesIds.length
      ? _.map(
          this.removedExchangeRatesIds,
          (removedExchangeRatesId: IExchangeRate["id"]) => {
            return this.$exchangeRatesMethods.updateRate(
              removedExchangeRatesId
            );
          }
        )
      : [Promise.resolve()];
  }

  // Computed

  public get availableExchangeRates(): IExchangeRate[] {
    return _.orderBy(
      _.filter(this.$exchangeRatesData.exchangeRates, exchangeRate =>
        _.some(this.orgCurrencies || [], ["id", exchangeRate.base_currency_id])
      ),
      ["code", "asc"]
    );
  }

  public get activeBaseCurrency() {
    return _.find(this.$exchangeRatesData.currencies, [
      "id",
      this.activeBaseCurrencyId
    ]);
  }

  public get orgCurrencies() {
    return this.$exchangeRatesData.orgBrandCurrencies;
  }

  public get orgExchangeRates() {
    return _.filter(this.$exchangeRatesData.exchangeRates, ["brand_id", null]);
  }

  public get brandExchangeRates() {
    return _.filter(
      this.$exchangeRatesData.exchangeRates,
      exchangeRate => !_.isNull(exchangeRate.brand_id)
    );
  }

  public get filteredCurrencies() {
    return _.filter(
      this.isOrgMode
        ? this.$exchangeRatesData.orgCurrencies
        : this.brandCurrencies,
      currency => currency.id !== this.activeBaseCurrencyId
    );
  }

  public get removedExchangeRatesIds() {
    return _.map(
      _.filter(this.currentExchangeRates, currentRate => {
        const initialRate = _.find(this.initialExchangeRates, [
          "currency_id",
          currentRate.currency_id
        ]);

        return currentRate.isDynamic && !initialRate?.isDynamic;
      }),
      filteredRate => filteredRate.id
    );
  }

  public get updatedExchangeRates() {
    return _.filter(this.currentExchangeRates, currentRate => {
      const initialRate = _.find(this.initialExchangeRates, [
        "currency_id",
        currentRate.currency_id
      ]);

      return (
        (!currentRate.isDynamic && initialRate?.isDynamic) ||
        (!initialRate?.isDynamic &&
          !currentRate.isDynamic &&
          currentRate.value !== initialRate?.value)
      );
    });
  }

  public get selectorIsActive() {
    return (
      _.isEmpty(this.updatedExchangeRates) &&
      _.isEmpty(this.removedExchangeRatesIds.length)
    );
  }

  public get hasEmptyValue() {
    return _.some(this.currentExchangeRates, ["value", ""]);
  }

  // Watchers

  @Watch("activeBaseCurrencyId")
  public onActiveBaseCurrencyChange() {
    this.initiateExchangeRates();
  }

  @Watch("updatedExchangeRates")
  public onUpdatedExchangeRatesChange() {
    this.onChange();
  }

  @Watch("removedExchangeRatesIds")
  public onRemovedExchangeRatesIds() {
    this.onChange();
  }

  @Watch("hasEmptyValue")
  public onHasEmptyValueChange() {
    this.$emit("error", this.hasEmptyValue);
  }
}
